// What the customer and EmmA said on the customer's Proposal page (14 Oct 2026), oldest first, with EmmA's
// hand-offs to the office marked. Read-only: the conversation is the record; an escalation already sits on the
// Opportunity's activity (or the Job's timeline) where staff pick it up.
import { useQuery } from "@tanstack/react-query";
import { authHeaders, fetchImpl } from "../lib/api";
import { env } from "../lib/env";
import { useT } from "../lib/i18n";

type Event = { id: number; kind: "message" | "escalation"; actor: string; detail: { text?: string; summary?: string }; created_at: string };
const when = (iso: string) => new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Sydney", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

export function ProposalPageChatLog({ proposalId }: { proposalId: string }) {
  const { t } = useT();
  const q = useQuery({ queryKey: ["proposal-page-chat", proposalId], enabled: !env.mock, refetchInterval: 60_000, queryFn: async () => {
    const r = await fetchImpl(`${env.apiBase}/api/proposals/${proposalId}/page-chat`, { headers: await authHeaders() });
    const b = await r.json() as { data?: Event[]; error?: string };
    if (!r.ok || !b.data) throw new Error(b.error ?? "Conversation unavailable");
    return b.data;
  } });
  if (env.mock) return null;
  const rows = (q.data ?? []).slice().reverse();
  return (
    <section className="mt-4 border-t pt-3">
      <h3 className="text-sm font-medium">{t("ppChat.title")}</h3>
      {q.isError && <p role="alert" className="text-sm text-destructive">{t("ppChat.failed")}</p>}
      {q.isSuccess && rows.length === 0 && <p className="text-sm text-muted-foreground">{t("ppChat.empty")}</p>}
      <ul className="mt-2 space-y-2">
        {rows.map((e) => e.kind === "escalation"
          ? <li key={e.id} className="rounded border border-gold/40 bg-gold/10 p-2 text-sm"><strong>{t("ppChat.handedOff")}</strong> {e.detail.summary} <span className="text-micro text-muted-foreground">· {when(e.created_at)}</span></li>
          : <li key={e.id} className={`max-w-[85%] rounded p-2 text-sm ${e.actor === "customer" ? "bg-muted" : "ml-auto bg-primary/10"}`}>
              <span className="block text-micro text-muted-foreground">{e.actor === "customer" ? t("ppChat.customer") : e.actor === "emma" ? "EmmA" : e.actor} · {when(e.created_at)}</span>
              <span className="whitespace-pre-wrap">{e.detail.text}</span>
            </li>)}
      </ul>
    </section>
  );
}
